/**
 * Application shell.
 *
 * Provides the persistent chrome every Torvanis Money screen renders inside:
 * branded header, primary navigation (a slide-over drawer below `lg`, a
 * collapsible rail above it), the routed content region, and the footer.
 * Feature slices add entries to {@link navigationSections} and a `<Route>`
 * below; they never re-implement the chrome.
 */

import clsx from 'clsx';
import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { BrandMark } from './components/BrandMark';
import { ErrorBoundary } from './components/ErrorBoundary';
import { NotificationBell } from './components/NotificationBell';
import { ROLE_LABELS, useAuth, useLogout, type AppRole } from './lib/auth';
// Every screen is a route-level code-split point (React.lazy): the bundle was
// a single 842KB chunk otherwise, a real first-load tax on a product whose
// flagship flow is one-handed receipt capture on a phone. Only the shell —
// navigation, session wall, error boundary — loads eagerly.
const Dashboard = lazy(() => import('./screens/Dashboard').then((m) => ({ default: m.Dashboard })));
const Login = lazy(() => import('./screens/Login').then((m) => ({ default: m.Login })));
const NotificationCenter = lazy(() =>
  import('./screens/NotificationCenter').then((m) => ({ default: m.NotificationCenter })),
);
const SystemStatus = lazy(() =>
  import('./screens/SystemStatus').then((m) => ({ default: m.SystemStatus })),
);
const AccountLedger = lazy(() =>
  import('./screens/accounts/AccountLedger').then((m) => ({ default: m.AccountLedger })),
);
const ChartOfAccounts = lazy(() =>
  import('./screens/accounts/ChartOfAccounts').then((m) => ({ default: m.ChartOfAccounts })),
);
const ApprovalWorkbench = lazy(() =>
  import('./screens/approvals/ApprovalWorkbench').then((m) => ({ default: m.ApprovalWorkbench })),
);
const Reimbursements = lazy(() =>
  import('./screens/approvals/Reimbursements').then((m) => ({ default: m.Reimbursements })),
);
const ApproverAssignments = lazy(() =>
  import('./screens/admin/ApproverAssignments').then((m) => ({ default: m.ApproverAssignments })),
);
const AuditLog = lazy(() =>
  import('./screens/admin/AuditLog').then((m) => ({ default: m.AuditLog })),
);
const CutoverSettings = lazy(() =>
  import('./screens/admin/CutoverSettings').then((m) => ({ default: m.CutoverSettings })),
);
const EmailOutbox = lazy(() =>
  import('./screens/admin/EmailOutbox').then((m) => ({ default: m.EmailOutbox })),
);
const PurgeQueue = lazy(() =>
  import('./screens/admin/PurgeQueue').then((m) => ({ default: m.PurgeQueue })),
);
const EntitySettings = lazy(() =>
  import('./screens/admin/EntitySettings').then((m) => ({ default: m.EntitySettings })),
);
const IntegrationSettings = lazy(() =>
  import('./screens/admin/IntegrationSettings').then((m) => ({ default: m.IntegrationSettings })),
);
const OpeningBalancesWizard = lazy(() =>
  import('./screens/admin/OpeningBalancesWizard').then((m) => ({
    default: m.OpeningBalancesWizard,
  })),
);
const QuickBooksImport = lazy(() =>
  import('./screens/admin/QuickBooksImport').then((m) => ({ default: m.QuickBooksImport })),
);
const RoleMappings = lazy(() =>
  import('./screens/admin/RoleMappings').then((m) => ({ default: m.RoleMappings })),
);
const SyncSettings = lazy(() =>
  import('./screens/admin/SyncSettings').then((m) => ({ default: m.SyncSettings })),
);
const BankAccounts = lazy(() =>
  import('./screens/banking/BankAccounts').then((m) => ({ default: m.BankAccounts })),
);
const CustomerForm = lazy(() =>
  import('./screens/customers/CustomerForm').then((m) => ({ default: m.CustomerForm })),
);
const VendorForm = lazy(() =>
  import('./screens/customers/CustomerForm').then((m) => ({ default: m.VendorForm })),
);
const AchPaymentPage = lazy(() =>
  import('./screens/invoices/AchPaymentPage').then((m) => ({ default: m.AchPaymentPage })),
);
const InvoiceDetail = lazy(() =>
  import('./screens/invoices/InvoiceDetail').then((m) => ({ default: m.InvoiceDetail })),
);
const InvoiceEditor = lazy(() =>
  import('./screens/invoices/InvoiceEditor').then((m) => ({ default: m.InvoiceEditor })),
);
const InvoiceList = lazy(() =>
  import('./screens/invoices/InvoiceList').then((m) => ({ default: m.InvoiceList })),
);
const PaymentStatus = lazy(() =>
  import('./screens/invoices/PaymentStatus').then((m) => ({ default: m.PaymentStatus })),
);
const PublicPayPage = lazy(() =>
  import('./screens/invoices/PublicPayPage').then((m) => ({ default: m.PublicPayPage })),
);
const ReportsHub = lazy(() =>
  import('./screens/reports/ReportsHub').then((m) => ({ default: m.ReportsHub })),
);
const TaxExport = lazy(() =>
  import('./screens/tax/TaxExport').then((m) => ({ default: m.TaxExport })),
);
const CustomerList = lazy(() =>
  import('./screens/customers/CustomerList').then((m) => ({ default: m.CustomerList })),
);
const VendorList = lazy(() =>
  import('./screens/vendors/VendorList').then((m) => ({ default: m.VendorList })),
);
const ImportTransactions = lazy(() =>
  import('./screens/banking/ImportTransactions').then((m) => ({ default: m.ImportTransactions })),
);
const TransactionList = lazy(() =>
  import('./screens/banking/TransactionList').then((m) => ({ default: m.TransactionList })),
);
const ExpenseDetail = lazy(() =>
  import('./screens/expenses/ExpenseDetail').then((m) => ({ default: m.ExpenseDetail })),
);
const ExpenseList = lazy(() =>
  import('./screens/expenses/ExpenseList').then((m) => ({ default: m.ExpenseList })),
);
const MatchReview = lazy(() =>
  import('./screens/expenses/MatchReview').then((m) => ({ default: m.MatchReview })),
);
const ReceiptCapture = lazy(() =>
  import('./screens/expenses/ReceiptCapture').then((m) => ({ default: m.ReceiptCapture })),
);
const ReceiptReview = lazy(() =>
  import('./screens/expenses/ReceiptReview').then((m) => ({ default: m.ReceiptReview })),
);

const SIDEBAR_STORAGE_KEY = 'tvm:shell:sidebar-collapsed';

export interface NavigationItem {
  to: string;
  label: string;
  description: string;
  icon: (props: { className?: string }) => JSX.Element;
  /** When present the entry is hidden unless the user holds one of these roles. */
  requiredRoles?: readonly AppRole[];
}

function ActivityIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M3 12h4l3 8 4-16 3 8h4" />
    </svg>
  );
}

function MenuIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M15 6l-6 6 6 6" />
    </svg>
  );
}

function LedgerIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M5 4h11l3 3v13H5z" />
      <path d="M8 9h8M8 13h8M8 17h5" />
    </svg>
  );
}

function ShieldIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M12 3l7 3v6c0 4.2-2.8 7.6-7 9-4.2-1.4-7-4.8-7-9V6l7-3z" />
      <path d="M9.5 12.2l1.8 1.8 3.4-3.6" />
    </svg>
  );
}

function ArchiveIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M3 6h18v3H3z" />
      <path d="M5 9v10h14V9" />
      <path d="M10 13h4" />
    </svg>
  );
}

function PlugIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M9 3v6M15 3v6" />
      <path d="M6 9h12v3a6 6 0 01-6 6 6 6 0 01-6-6z" />
      <path d="M12 18v3" />
    </svg>
  );
}

function BankIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M3 9l9-5 9 5" />
      <path d="M5 9v9M9.5 9v9M14.5 9v9M19 9v9" />
      <path d="M3 20h18" />
    </svg>
  );
}

function InvoiceIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M6 3h12v18l-3-2-3 2-3-2-3 2V3z" />
      <path d="M9 8h6M9 12h6M9 16h3" />
    </svg>
  );
}

function DirectoryIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M9 11a3 3 0 100-6 3 3 0 000 6z" />
      <path d="M3 20a6 6 0 0112 0" />
      <path d="M16 8h5M16 12h5M16 16h5" />
    </svg>
  );
}

function UploadIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M12 16V4" />
      <path d="M8 8l4-4 4 4" />
      <path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
    </svg>
  );
}

function ExchangeIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M4 8h13l-3-3" />
      <path d="M20 16H7l3 3" />
    </svg>
  );
}

function MailIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3.5 7l8.5 6 8.5-6" />
    </svg>
  );
}

function DashboardIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M4 20V10" />
      <path d="M10 20V4" />
      <path d="M16 20v-7" />
      <path d="M22 20H2" />
    </svg>
  );
}

function ReportIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M6 3h9l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
      <path d="M14 3v6h6" />
      <path d="M9 17v-3" />
      <path d="M12 17v-6" />
      <path d="M15 17v-4" />
    </svg>
  );
}

/** Registry the router and both navigation surfaces read from. */
export const navigationSections: readonly NavigationItem[] = [
  {
    to: '/dashboard',
    label: 'Dashboard',
    description: 'Cash, receivables, payables, and cash flow at a glance',
    icon: DashboardIcon,
    requiredRoles: ['owner'],
  },
  {
    to: '/',
    label: 'Service overview',
    description: 'Platform health and deployed release',
    icon: ActivityIcon,
  },
  {
    to: '/accounts',
    label: 'Chart of accounts',
    description: 'The general ledger accounts money is coded to',
    icon: LedgerIcon,
    requiredRoles: ['owner', 'finance'],
  },
  {
    to: '/expenses',
    label: 'Expenses',
    description: 'Receipts, drafts, and what is awaiting approval',
    icon: ReceiptIcon,
  },
  {
    to: '/expenses/matches',
    label: 'Match review',
    description: 'Receipts the matcher paired with a bank transaction',
    icon: ExchangeIcon,
  },
  {
    to: '/approvals',
    label: 'Approvals',
    description: 'Review receipts and post them to the ledger',
    icon: ApprovalIcon,
    requiredRoles: ['owner', 'finance', 'approver'],
  },
  {
    to: '/approvals/reimbursements',
    label: 'Reimbursements',
    description: 'Who is owed money for approved expenses, and paying it back',
    icon: ApprovalIcon,
    requiredRoles: ['owner', 'finance'],
  },
  {
    to: '/banking/accounts',
    label: 'Bank accounts',
    description: 'Linked banks and cards, and how fresh the feed is',
    icon: BankIcon,
    requiredRoles: ['owner', 'finance'],
  },
  {
    to: '/banking/transactions',
    label: 'Bank transactions',
    description: 'Everything imported from the bank, filtered how you like',
    icon: ExchangeIcon,
    requiredRoles: ['owner', 'finance'],
  },
  {
    to: '/banking/import',
    label: 'Import statement',
    description: 'Load an OFX or CSV export when a bank cannot be connected',
    icon: UploadIcon,
    requiredRoles: ['owner', 'finance'],
  },
  {
    to: '/reports',
    label: 'Reports',
    description: 'P&L, balance sheet, cash flow, and aging — with CSV and PDF export',
    icon: ReportIcon,
    requiredRoles: ['owner', 'finance'],
  },
  {
    to: '/tax',
    label: 'Tax export',
    description: 'Workpaper, Form 1065, K-1s, and the Ohio returns for a tax year',
    icon: ReportIcon,
    requiredRoles: ['owner'],
  },
  {
    to: '/invoices',
    label: 'Invoices',
    description: 'What customers owe Torvanis, from draft to paid',
    icon: InvoiceIcon,
    requiredRoles: ['owner', 'finance'],
  },
  {
    to: '/customers',
    label: 'Customers',
    description: 'Who Torvanis invoices, and the account their revenue lands in',
    icon: DirectoryIcon,
    requiredRoles: ['owner', 'finance'],
  },
  {
    to: '/vendors',
    label: 'Vendors',
    description: 'Who Torvanis buys from, including names read off receipts',
    icon: DirectoryIcon,
    requiredRoles: ['owner', 'finance'],
  },
  {
    to: '/notifications',
    label: 'Notifications',
    description: 'Approvals, overdue reviews, and delivery problems',
    icon: BellIcon,
  },
  {
    to: '/admin/approvers',
    label: 'Approvers',
    description: 'Who approves whom, and who skips review',
    icon: ApprovalIcon,
    requiredRoles: ['owner', 'finance'],
  },
  {
    to: '/admin/quickbooks-import',
    label: 'QuickBooks import',
    description: 'Bring the chart, contacts, and history across from QuickBooks',
    icon: UploadIcon,
    requiredRoles: ['owner'],
  },
  {
    to: '/admin/opening-balances',
    label: 'Opening balances',
    description: 'The one dated entry the books start from',
    icon: LedgerIcon,
    requiredRoles: ['owner'],
  },
  {
    to: '/admin/cutover',
    label: 'Cutover date',
    description: 'Where imported history ends and native records begin',
    icon: ShieldIcon,
    requiredRoles: ['owner'],
  },
  {
    to: '/admin/entity-settings',
    label: 'Entity settings',
    description: 'Legal name, EIN, and the partners the tax forms are built from',
    icon: ShieldIcon,
    requiredRoles: ['owner'],
  },
  {
    to: '/admin/audit',
    label: 'Audit log',
    description: 'Who changed what, with the values either side',
    icon: ShieldIcon,
    requiredRoles: ['owner'],
  },
  {
    to: '/admin/integrations',
    label: 'Integration settings',
    description: 'Plaid, Stripe, AI, and email credentials',
    icon: PlugIcon,
    requiredRoles: ['owner'],
  },
  {
    to: '/admin/sync',
    label: 'Sync settings',
    description: 'How often the bank feed refreshes, and what ran when',
    icon: ExchangeIcon,
    requiredRoles: ['owner'],
  },
  {
    to: '/admin/email-outbox',
    label: 'Email outbox',
    description: 'Delivery status, errors, and manual retries',
    icon: MailIcon,
    requiredRoles: ['owner'],
  },
  {
    to: '/admin/role-mappings',
    label: 'Role mappings',
    description: 'Authentik groups that grant each role',
    icon: ShieldIcon,
    requiredRoles: ['owner'],
  },
  {
    to: '/admin/purge-queue',
    label: 'Purge review',
    description: 'Records past seven years, waiting on a keep-or-delete decision',
    icon: ArchiveIcon,
    requiredRoles: ['owner'],
  },
];

function BellIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M18 8a6 6 0 1 0-12 0c0 5-2 6-2 6h16s-2-1-2-6" />
      <path d="M10.3 20a2 2 0 0 0 3.4 0" />
    </svg>
  );
}

function ApprovalIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M9 11l3 3 6-6" />
      <path d="M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h8" />
    </svg>
  );
}

function ReceiptIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M6 3h12v18l-3-2-3 2-3-2-3 2z" />
      <path d="M9 8h6M9 12h6M9 16h3" />
    </svg>
  );
}

function BrandLockup(): JSX.Element {
  return (
    <Link
      to="/"
      className="flex min-h-touch items-center gap-3 rounded-md px-1 text-left"
      aria-label="Torvanis Money home"
    >
      <BrandMark className="h-8 w-8 shrink-0" />
      <span className="flex flex-col leading-tight">
        <span className="text-sm font-bold uppercase tracking-[0.18em] text-surface-raised">
          Torvanis
        </span>
        <span className="text-xs font-medium uppercase tracking-[0.3em] text-brand-orange">
          Money
        </span>
      </span>
    </Link>
  );
}

/**
 * Whether the sidebar entry for `to` should light up on `pathname`.
 *
 * A blanket `end` prop kept detail routes (`/expenses/capture`,
 * `/invoices/:id`, `/accounts/:id/ledger`, …) from highlighting anything, but
 * plain prefix matching cannot work either: `/expenses` and
 * `/expenses/matches` (and `/approvals` vs `/approvals/reimbursements`) are
 * separate entries. So: exact-or-prefix match, longest matching entry wins,
 * and the home entry `/` only matches itself.
 */
export function isNavigationItemActive(to: string, pathname: string): boolean {
  if (to === '/') {
    return pathname === '/';
  }
  const matches = pathname === to || pathname.startsWith(`${to}/`);
  if (!matches) {
    return false;
  }
  return !navigationSections.some(
    (other) =>
      other.to.length > to.length && (pathname === other.to || pathname.startsWith(`${other.to}/`)),
  );
}

function NavigationList({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
}): JSX.Element {
  const { hasRole } = useAuth();
  const { pathname } = useLocation();
  const visible = navigationSections.filter(
    (item) => item.requiredRoles === undefined || hasRole(...item.requiredRoles),
  );

  return (
    <nav aria-label="Primary" className="flex flex-col gap-1 p-3">
      {visible.map((item) => {
        const active = isNavigationItemActive(item.to, pathname);
        return (
          <Link
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            title={collapsed ? item.label : undefined}
            aria-current={active ? 'page' : undefined}
            className={clsx(
              'group flex min-h-touch items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-150 ease-standard',
              collapsed && 'lg:justify-center lg:px-2',
              active
                ? 'bg-brand-red text-surface-raised'
                : 'text-ink-300 hover:bg-surface-inverse-raised hover:text-surface-raised',
            )}
          >
            <item.icon className="h-5 w-5 shrink-0" />
            <span className={clsx('flex flex-col', collapsed && 'lg:sr-only')}>
              <span>{item.label}</span>
              <span className="text-xs font-normal opacity-80">{item.description}</span>
            </span>
          </Link>
        );
      })}
    </nav>
  );
}

function NotFound(): JSX.Element {
  return (
    <section className="tvm-card flex max-w-xl flex-col items-start gap-4 p-6">
      <h2 className="text-xl font-semibold text-ink-900">That page isn&rsquo;t here</h2>
      <p className="text-sm leading-relaxed text-ink-700">
        The address you followed doesn&rsquo;t match anything in Torvanis Money. It may have been a
        typo, or the feature may not have shipped yet.
      </p>
      <Link to="/" className="tvm-button-primary">
        Back to service overview
      </Link>
    </section>
  );
}

/**
 * Everything inside the shell needs a session. An anonymous visitor is sent to
 * the sign-in screen with their destination preserved; a service that is simply
 * unreachable gets an explanation and a retry rather than a redirect loop.
 */
function RequireSession({ children }: { children: JSX.Element }): JSX.Element {
  const auth = useAuth();
  const location = useLocation();

  if (auth.isLoading) {
    return (
      <div className="space-y-4" aria-busy="true">
        <div className="tvm-skeleton h-8 w-56" />
        <div className="tvm-skeleton h-40 w-full" />
        <span className="sr-only">Checking your session…</span>
      </div>
    );
  }

  if (auth.error !== null && auth.error.status !== 401) {
    return (
      <section className="tvm-card max-w-xl space-y-3 p-6" role="alert">
        <h1 className="text-xl font-semibold text-ink-900">We can&rsquo;t confirm your session</h1>
        <p className="text-sm leading-relaxed text-ink-700">{auth.error.message}</p>
        <button
          type="button"
          className="tvm-button-primary"
          onClick={() => window.location.reload()}
        >
          Reload
        </button>
      </section>
    );
  }

  if (!auth.isAuthenticated) {
    const destination = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?returnTo=${encodeURIComponent(destination)}`} replace />;
  }

  return children;
}

/** Signed-in identity and the sign-out control, in the header. */
function SessionChip(): JSX.Element | null {
  const { user, isAuthenticated } = useAuth();
  const { signOut, isPending } = useLogout();

  if (!isAuthenticated || user === null) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      <span className="hidden flex-col text-right leading-tight sm:flex">
        <span className="text-xs font-semibold text-surface-raised">
          {user.displayName ?? user.email ?? user.subject}
        </span>
        <span className="text-[0.65rem] uppercase tracking-wide text-ink-300">
          {user.roles.map((role) => ROLE_LABELS[role]).join(' · ')}
        </span>
      </span>
      <button
        type="button"
        className="tvm-button-ghost px-3 text-ink-300 hover:bg-surface-inverse-raised hover:text-surface-raised"
        onClick={signOut}
        disabled={isPending}
      >
        {isPending ? 'Signing out…' : 'Sign out'}
      </button>
    </div>
  );
}

function readStoredCollapsed(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true';
  } catch {
    // Private-browsing modes can throw on storage access; the default is fine.
    return false;
  }
}

/**
 * Route table. `/login` renders outside the chrome — there is nothing to
 * navigate to until someone is signed in. `/pay/:token/bank-transfer` renders
 * outside the chrome *and* outside the session wall: it is the page behind the
 * bank-transfer link in the invoice email, opened by an external customer who
 * has no account here. The signed token in the URL is its authorisation
 * (FR-12, AC-5).
 */
export function App(): JSX.Element {
  return (
    <Suspense fallback={<ScreenLoading />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/pay/:token/bank-transfer" element={<PublicPayPage />} />
        <Route path="/pay/:token" element={<PublicPayPage />} />
        <Route path="*" element={<AppShell />} />
      </Routes>
    </Suspense>
  );
}

/**
 * What a route-level chunk shows while it downloads. Deliberately quiet — on
 * any decent connection it flashes for tens of milliseconds.
 */
function ScreenLoading(): JSX.Element {
  return (
    <div className="flex min-h-[40vh] items-center justify-center" aria-busy="true">
      <span className="sr-only">Loading…</span>
      <span
        aria-hidden="true"
        className="h-8 w-8 animate-spin rounded-full border-2 border-line-subtle border-t-brand-red"
      />
    </div>
  );
}

function AppShell(): JSX.Element {
  const location = useLocation();
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [isCollapsed, setCollapsed] = useState<boolean>(readStoredCollapsed);

  // Route changes always close the mobile drawer, so a tap never leaves the
  // overlay covering the screen the user just navigated to.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isDrawerOpen) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setDrawerOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isDrawerOpen]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((previous) => {
      const next = !previous;
      try {
        window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next));
      } catch {
        // Preference is best-effort; the layout still toggles for this session.
      }
      return next;
    });
  }, []);

  return (
    <div className="flex min-h-dvh flex-col bg-surface-base">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-surface-raised focus:px-4 focus:py-2 focus:text-sm focus:font-semibold"
      >
        Skip to content
      </a>

      <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-line-inverse bg-surface-inverse px-3 py-2 sm:px-4">
        <button
          type="button"
          className="tvm-button-ghost min-w-touch px-2 text-ink-300 hover:bg-surface-inverse-raised hover:text-surface-raised lg:hidden"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open navigation"
          aria-expanded={isDrawerOpen}
          aria-controls="primary-navigation-drawer"
        >
          <MenuIcon className="h-5 w-5" />
        </button>

        <BrandLockup />

        <div className="ml-auto flex items-center gap-2">
          {import.meta.env.DEV && (
            <span className="tvm-pill hidden bg-brand-orange-soft text-brand-red-strong sm:inline-flex">
              Development
            </span>
          )}
          <NotificationBell />
          <SessionChip />
        </div>
      </header>

      <div className="flex flex-1">
        <aside
          className={clsx(
            'hidden shrink-0 border-r border-line-inverse bg-surface-inverse transition-[width] duration-200 ease-standard lg:flex lg:flex-col',
            isCollapsed ? 'lg:w-[4.5rem]' : 'lg:w-72',
          )}
        >
          <NavigationList collapsed={isCollapsed} />
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-pressed={isCollapsed}
            className="mt-auto flex min-h-touch items-center gap-2 border-t border-line-inverse px-4 text-xs font-semibold uppercase tracking-wide text-ink-300 transition-colors duration-150 ease-standard hover:bg-surface-inverse-raised hover:text-surface-raised"
          >
            <ChevronIcon
              className={clsx('h-4 w-4 transition-transform', isCollapsed && 'rotate-180')}
            />
            <span className={clsx(isCollapsed && 'sr-only')}>Collapse</span>
          </button>
        </aside>

        {isDrawerOpen && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <button
              type="button"
              className="absolute inset-0 h-full w-full cursor-default bg-surface-inverse/60"
              aria-label="Close navigation"
              onClick={() => setDrawerOpen(false)}
            />
            <div
              id="primary-navigation-drawer"
              role="dialog"
              aria-modal="true"
              aria-label="Primary navigation"
              className="relative flex h-full w-72 max-w-[85vw] flex-col bg-surface-inverse shadow-raised"
            >
              <div className="flex items-center justify-between border-b border-line-inverse px-3 py-2">
                <BrandLockup />
                <button
                  type="button"
                  className="tvm-button-ghost min-w-touch px-2 text-ink-300 hover:bg-surface-inverse-raised hover:text-surface-raised"
                  onClick={() => setDrawerOpen(false)}
                  aria-label="Close navigation"
                >
                  <CloseIcon className="h-5 w-5" />
                </button>
              </div>
              <NavigationList collapsed={false} onNavigate={() => setDrawerOpen(false)} />
            </div>
          </div>
        )}

        <main id="main-content" className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <RequireSession>
            {/* Inside the chrome on purpose: a screen that crashes at render
                time (e.g. a 200 whose shape drifted mid-deploy) must not take
                the navigation down with it. Keyed to reset on route change. */}
            <ErrorBoundary resetKey={location.pathname}>
              {/* Inner boundary: switching routes inside the chrome must not
                  blank the navigation while the next screen's chunk loads. */}
              <Suspense fallback={<ScreenLoading />}>
                <Routes>
                  <Route path="/" element={<SystemStatus />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/reports" element={<ReportsHub />} />
                  <Route path="/tax" element={<TaxExport />} />
                  <Route path="/accounts" element={<ChartOfAccounts />} />
                  <Route path="/accounts/:accountId/ledger" element={<AccountLedger />} />
                  <Route path="/expenses" element={<ExpenseList />} />
                  <Route path="/expenses/new" element={<ExpenseDetail />} />
                  <Route path="/expenses/capture" element={<ReceiptCapture />} />
                  <Route path="/expenses/matches" element={<MatchReview />} />
                  <Route path="/expenses/:expenseId" element={<ExpenseDetail />} />
                  <Route path="/expenses/:expenseId/review" element={<ReceiptReview />} />
                  <Route path="/approvals" element={<ApprovalWorkbench />} />
                  <Route path="/approvals/reimbursements" element={<Reimbursements />} />
                  <Route path="/approvals/:expenseId" element={<ApprovalWorkbench />} />
                  <Route path="/banking/accounts" element={<BankAccounts />} />
                  <Route path="/banking/transactions" element={<TransactionList />} />
                  <Route path="/banking/import" element={<ImportTransactions />} />
                  <Route path="/invoices" element={<InvoiceList />} />
                  <Route path="/invoices/new" element={<InvoiceEditor />} />
                  <Route path="/invoices/:id" element={<InvoiceDetail />} />
                  <Route path="/invoices/:id/edit" element={<InvoiceEditor />} />
                  <Route path="/invoices/:id/payment" element={<PaymentStatus />} />
                  <Route path="/invoices/:id/payment/bank-transfer" element={<AchPaymentPage />} />
                  <Route path="/customers" element={<CustomerList />} />
                  <Route path="/customers/new" element={<CustomerForm />} />
                  <Route path="/customers/:id" element={<CustomerForm />} />
                  <Route path="/vendors" element={<VendorList />} />
                  <Route path="/vendors/new" element={<VendorForm />} />
                  <Route path="/vendors/:id" element={<VendorForm />} />
                  <Route path="/notifications" element={<NotificationCenter />} />
                  <Route path="/admin/approvers" element={<ApproverAssignments />} />
                  <Route path="/admin/audit" element={<AuditLog />} />
                  <Route path="/admin/entity-settings" element={<EntitySettings />} />
                  <Route path="/admin/email-outbox" element={<EmailOutbox />} />
                  <Route path="/admin/integrations" element={<IntegrationSettings />} />
                  <Route path="/admin/quickbooks-import" element={<QuickBooksImport />} />
                  <Route path="/admin/opening-balances" element={<OpeningBalancesWizard />} />
                  <Route path="/admin/cutover" element={<CutoverSettings />} />
                  <Route path="/admin/role-mappings" element={<RoleMappings />} />
                  <Route path="/admin/sync" element={<SyncSettings />} />
                  <Route path="/admin/purge-queue" element={<PurgeQueue />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </RequireSession>
        </main>
      </div>

      <footer className="border-t border-line-subtle bg-surface-raised px-4 py-4 text-xs text-ink-500 sm:px-6 lg:px-8">
        <p>
          Torvanis Money — internal financial system of the Torvanis partnership. Figures are
          bookkeeping records, not tax advice.
        </p>
      </footer>
    </div>
  );
}
